import { z } from 'zod';

const Count = z.number().int().nonnegative().safe().nullable();
export const GoogleModalityTokensSchema = z.object({
  modality: z.string().regex(/^[A-Z_]+$/).max(64),
  tokenCount: z.number().int().nonnegative().safe(),
}).strict();

/** Redacted provider measurements only. Null means absent, never zero. */
export const GoogleTokenUsageSchema = z.object({
  schemaVersion: z.literal(1),
  source: z.enum(['provider_reported', 'unknown']),
  state: z.enum(['reported', 'partial', 'unknown', 'invalid']),
  promptTokenCount: Count,
  cachedContentTokenCount: Count,
  candidatesTokenCount: Count,
  thoughtsTokenCount: Count,
  toolUsePromptTokenCount: Count,
  totalTokenCount: Count,
  promptTokensDetails: z.array(GoogleModalityTokensSchema).nullable(),
  cacheTokensDetails: z.array(GoogleModalityTokensSchema).nullable(),
  candidatesTokensDetails: z.array(GoogleModalityTokensSchema).nullable(),
  toolUsePromptTokensDetails: z.array(GoogleModalityTokensSchema).nullable(),
  serviceTier: z.string().max(64).nullable(),
}).strict();
export type GoogleTokenUsage = z.infer<typeof GoogleTokenUsageSchema>;
export type GoogleModalityTokens = z.infer<typeof GoogleModalityTokensSchema>;

const Rates = z.object({
  TEXT: z.number().nonnegative(),
  IMAGE: z.number().nonnegative(),
  AUDIO: z.number().nonnegative(),
  VIDEO: z.number().nonnegative(),
}).strict();
export const GooglePriceSnapshotSchema = z.object({
  schemaVersion: z.literal(1),
  revision: z.string(),
  model: z.string(),
  api: z.literal('gemini_developer_v1beta'),
  source: z.literal('official_documentation'),
  evidenceUrl: z.string().url(),
  verifiedAt: z.string().datetime(),
  currency: z.literal('USD'),
  unit: z.literal('per_million_tokens'),
  serviceTier: z.enum(['standard', 'flex', 'priority']),
  contextTier: z.enum(['all', 'up_to_200k', 'over_200k']),
  input: Rates,
  cachedInput: Rates.nullable(),
  outputTextIncludingThoughts: z.number().nonnegative().nullable(),
}).strict();
export type GooglePriceSnapshot = z.infer<typeof GooglePriceSnapshotSchema>;

export const GoogleValuationSchema = z.object({
  status: z.enum(['complete', 'partial', 'unknown', 'conflict']),
  costUsd: z.number().nonnegative().nullable(),
  knownCostUsd: z.number().nonnegative(),
  reasons: z.array(z.string()),
  price: GooglePriceSnapshotSchema.nullable(),
}).strict();
export type GoogleValuation = z.infer<typeof GoogleValuationSchema>;

const CostBreakdownSchema = z.object({
  service: z.string(), model: z.string(), calls: z.number(),
  input_tokens: z.number().nullable(), output_tokens: z.number().nullable(),
  cost_usd: z.number().nullable(), known_cost_usd: z.number(),
  incomplete_calls: z.number(),
});
export const GoogleCostSummarySchema = z.object({
  tenant_id: z.string().uuid(), from_date: z.string(), to_date: z.string(),
  currency: z.literal('USD'), valuation: z.literal('reported_usage_list_price'),
  total_cost_usd: z.number().nullable(), known_cost_usd: z.number(),
  total_calls: z.number(), incomplete_calls: z.number(),
  legacy_calls: z.number(), legacy_estimated_cost_usd: z.number(),
  daily_quota_usd: z.number().nullable(), today_spent_usd: z.number().nullable(),
  today_known_cost_usd: z.number().nullable(), today_incomplete_calls: z.number().nullable(),
  by_service_model: z.array(CostBreakdownSchema),
  by_day: z.array(z.object({
    day: z.string(), cost_usd: z.number().nullable(), known_cost_usd: z.number(),
    calls: z.number(), incomplete_calls: z.number(),
  })),
});
export type GoogleCostSummary = z.infer<typeof GoogleCostSummarySchema>;
